/**
 * SIMS-8783 AC1 — the merged Cups & Analytes screen: which tab is active, held in the URL.
 *
 * The Hi-Fi keeps the tab in component state (markup 1239) and therefore loses it on reload.
 * TestRail C17944 requires the selection to survive a reload, so the URL carries it instead — the
 * design does not settle this and the case does.
 *
 * `/cups` is the merged screen. `/analytes` no longer serves a standalone screen (C17945); it
 * redirects here with the Analytes tab selected, which keeps existing links and bookmarks alive.
 */
export const CUPS_ANALYTES_PATH = "/cups";
export const LEGACY_ANALYTES_PATH = "/analytes";

export type MergedTab = "cups" | "analytes";

const DEFAULT_TAB: MergedTab = "cups";
const TAB_PARAM = "tab";

/**
 * The tab named by a query string, defaulting to Cups.
 *
 * Deliberately strict: only the exact lower-case token is honoured. An unknown, empty or
 * differently-cased value falls back to Cups rather than rendering an empty screen, because this
 * value arrives from the address bar and cannot be trusted to be well formed.
 */
export const tabFromSearch = (search: string): MergedTab => {
	const value = new URLSearchParams(search).get(TAB_PARAM);
	return value === "analytes" ? "analytes" : DEFAULT_TAB;
};

/**
 * The query string for a tab. The default tab is left OUT so `/cups` stays the clean canonical
 * link and does not gain a redundant `?tab=cups` the first time someone touches the toggle.
 */
export const searchForTab = (tab: MergedTab): string =>
	tab === DEFAULT_TAB ? "" : `?${TAB_PARAM}=${tab}`;

/** Where the retired `/analytes` route sends the user (C17945). */
export const analytesRedirectTarget = (): string =>
	`${CUPS_ANALYTES_PATH}${searchForTab("analytes")}`;
